import { EOnboardingType } from "@/account/account-models";
import { IHeaderModalActions } from "@/common/header-modal/header-modal-actions";
import { ILandingActions } from "@/landing/landing-actions";
import type { IDictionary, IPopupConfig } from "@/models";
import type { IGetRequest, IPostRequest, IRequest } from "@/utils/async";
import { ERequestStatus, requestInit } from "@/utils/async";
import { sendWarningMessage } from "@/utils/slack";
import { defined } from "@/utils/variable-evaluation";
import { createReducer } from "@reduxjs/toolkit";
import type { IGetSubscriptionsConfig } from "./account-action-creators";
import { IAccountActions } from "./account-actions";
import type {
  IAutoMessageCategory,
  ICreditCard,
  IIdVerificationInfo,
  IMyPurchase,
  IOnboardingConfig,
  IProviderConfig,
  ISubscriptionInfo,
  IUserInfo,
  IVaultMedia,
} from "./account-models";
import { EAutoMessageType, ESubscriptionInfoStatus } from "./account-models";

export enum EUpdateUserType {
  Notifications = "Notifications",
  Profile = "Profile",
}

export const UPDATE_USER_TYPE = {
  invisible: EUpdateUserType.Profile,
  intro_enabled: EUpdateUserType.Profile,
  marketing: EUpdateUserType.Notifications,
  notifications: EUpdateUserType.Notifications,
  payments: EUpdateUserType.Notifications,
  payouts: EUpdateUserType.Notifications,
};

export interface IAccountModel {
  onboardingConfig: IOnboardingConfig | null;
  user: IUserInfo | null;
  providerConfig: IProviderConfig | null;
  getUser: IGetRequest<any>;
  getProviderConfig: IGetRequest<any>;
  getUsernameAvailable: IGetRequest<any>;
  patchUpdateUser: IPostRequest<any, any>;
  patchUpdateUserNotifications: IPostRequest<any, any>;
  patchUpdateUserProfile: IPostRequest<any, any>;
  getGeoIp: IGetRequest<any>;
  postGetCredits: IPostRequest<any, any>;
  deleteUserLogout: IRequest<any>;
  accountPopup: IPopupConfig | null;
  onboardingPopup: IPopupConfig | null;
  getSavedCreditCards: IGetRequest<any>;
  savedCreditCards: ICreditCard[];
  deleteSavedCreditCard: IRequest<any>;
  getSubscriptions: IGetRequest<any>;
  nextSubscriptionsUrl: string | null;
  subscriptions: ISubscriptionInfo[] | null;
  subscriptionsTotal: number;
  // deleteSubscription: IRequest<any>;
  deleteVideoGreeting: IRequest<any>;
  isManageSubscriptionsEditing: boolean;
  // isManageSubscriptionsSelectAll: boolean;
  manageSubscriptionsSelection: ISubscriptionInfo[];
  deleteSubscriptions: IRequest<any>;
  patchReenableSubscription: IRequest<any>;
  getRandomAvatar: IGetRequest<any>;
  autoMessageCategories: IAutoMessageCategory[];
  lastSavedAutoMessageCategories: IAutoMessageCategory[];
  getAutoMessages: IGetRequest<any>;
  postAutoMessage: IPostRequest<any, any>;
  deleteAutoMessage: IRequest<any>;
  deleteAutoMessages: IRequest<any>;
  putAutoMessages: IPostRequest<any, any>;
  postUserTag: IPostRequest<any, any>;
  getMyPurchases: IGetRequest<any>;
  getMyPurchasesNext: IGetRequest<any>;
  nextMyPurchasesUrl: string;
  myPurchases: IMyPurchase[];
  patchHideMyPurchase: IRequest<any>;
  patchUnhideMyPurchase: IRequest<any>;
  getIdVerification: IGetRequest<any>;
  idVerification: IIdVerificationInfo | null;
  getVaultMedia: IGetRequest<any>;
  getVaultMediaNext: IGetRequest<any>;
  nextGetVaultMediaUrl: string;
  vaultMedia: IVaultMedia[];
  deleteVaultMedia: IRequest<any>;
}

const INITIAL_STATE: IAccountModel = {
  onboardingConfig: null,
  user: null,
  providerConfig: null,
  getUser: requestInit(),
  getProviderConfig: requestInit(),
  getUsernameAvailable: requestInit(),
  patchUpdateUser: requestInit(),
  patchUpdateUserNotifications: requestInit(),
  patchUpdateUserProfile: requestInit(),
  getGeoIp: requestInit(),
  postGetCredits: requestInit(),
  deleteUserLogout: requestInit(),
  accountPopup: null,
  onboardingPopup: null,
  getSavedCreditCards: requestInit(),
  savedCreditCards: [],
  deleteSavedCreditCard: requestInit(),
  getSubscriptions: requestInit(),
  nextSubscriptionsUrl: null,
  subscriptions: null,
  subscriptionsTotal: 0,
  // deleteSubscription: requestInit(),
  deleteVideoGreeting: requestInit(),
  isManageSubscriptionsEditing: false,
  // isManageSubscriptionsSelectAll: false,
  manageSubscriptionsSelection: [],
  deleteSubscriptions: requestInit(),
  patchReenableSubscription: requestInit(),
  getRandomAvatar: requestInit(),
  autoMessageCategories: [],
  lastSavedAutoMessageCategories: [],
  getAutoMessages: requestInit(),
  postAutoMessage: requestInit(),
  deleteAutoMessage: requestInit(),
  deleteAutoMessages: requestInit(),
  putAutoMessages: requestInit(),
  postUserTag: requestInit(),
  getMyPurchases: requestInit(),
  getMyPurchasesNext: requestInit(),
  nextMyPurchasesUrl: "",
  myPurchases: [],
  patchHideMyPurchase: requestInit(),
  patchUnhideMyPurchase: requestInit(),
  getIdVerification: requestInit(),
  idVerification: null,
  getVaultMedia: requestInit(),
  getVaultMediaNext: requestInit(),
  nextGetVaultMediaUrl: "",
  vaultMedia: [],
  deleteVaultMedia: requestInit(),
};

function patchUpdateUserKey(newValues: IDictionary<any>) {
  const value = Object.keys(newValues)[0];
  let asyncKey = "patchUpdateUser";
  if (defined((UPDATE_USER_TYPE as any)[value])) {
    asyncKey = `${asyncKey}${(UPDATE_USER_TYPE as any)[value]}`;
  }
  return asyncKey;
}

/** Reducer */
export const accountReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(
      IAccountActions.setUserOnboarding,
      (state, action: any): IAccountModel => {
        const newOnboardingConfig = {
          ...state.onboardingConfig,
          ...action.payload.config,
        };
        return {
          ...state,
          onboardingConfig: newOnboardingConfig,
        };
      }
    )
    .addCase(
      IAccountActions.resetUserOnboarding,
      (state, action: any): IAccountModel => {
        let newOnboardingConfig: IOnboardingConfig | null = {
          ...state.onboardingConfig,
        };
        if (action.payload.type === EOnboardingType.Init) {
          delete newOnboardingConfig.init;
        } else {
          delete newOnboardingConfig.complete;
        }
        // check if there is any onboarding left
        if (
          !defined(newOnboardingConfig.init) &&
          !defined(newOnboardingConfig.complete)
        ) {
          newOnboardingConfig = null; // reset
        }
        return {
          ...state,
          onboardingConfig: newOnboardingConfig,
        };
      }
    )
    .addCase(
      IAccountActions.resetUpdateUserNotifications,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchUpdateUserNotifications: requestInit(),
        };
      }
    )
    .addCase(
      IAccountActions.resetUpdateUserProfile,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchUpdateUserProfile: requestInit(),
        };
      }
    )
    // .addCase(IAccountActions.setUser, (state, action: any): IAccountModel => {
    //   return {
    //     ...state,
    //     user: action.payload.user
    //   };
    // })
    .addCase(
      IAccountActions.initGetUser,
      (state, action: any): IAccountModel => {
        const { isForceLoader } = action.payload;
        return {
          ...state,
          getUser: {
            status: ERequestStatus.Fetching,
          },
          user: isForceLoader ? null : state.user,
        };
      }
    )
    .addCase(
      IAccountActions.successGetUser,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getUser: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          user: action.payload,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetUser,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getUser: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetProviderConfig,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getProviderConfig: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetProviderConfig,
      (state, action: any): IAccountModel => {
        const providerConfig: IProviderConfig = action.payload;
        return {
          ...state,
          getProviderConfig: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          providerConfig,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetProviderConfig,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getProviderConfig: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetUsernameAvailable,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getUsernameAvailable: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetUsernameAvailable,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getUsernameAvailable: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failureGetUsernameAvailable,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getUsernameAvailable: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.resetPatchUpdateUser,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchUpdateUser: requestInit(),
          patchUpdateUserNotifications: requestInit(),
          patchUpdateUserProfile: requestInit(),
        };
      }
    )
    .addCase(
      IAccountActions.initPatchUpdateUser,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          [patchUpdateUserKey(action.payload.newValues)]: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPatchUpdateUser,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          [patchUpdateUserKey(action.payload.newValues)]: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          getGeoIp: requestInit(),
          user: action.payload.res, // updating user object here!
          onboardingConfig: null,
        };
      }
    )
    .addCase(
      IAccountActions.failurePatchUpdateUser,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          [patchUpdateUserKey(action.payload.newValues)]: {
            error: action.payload.error,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initPostGetCredits,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          postGetCredits: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPostGetCredits,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          postGetCredits: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failurePostGetCredits,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          postGetCredits: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.resetPostGetCredits,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          postGetCredits: requestInit(),
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteUserLogout,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteUserLogout: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteUserLogout,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteUserLogout: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          user: null,
        };
      }
    )
    .addCase(
      IAccountActions.errorDeleteUserLogout,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteUserLogout: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetGeoIp,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getGeoIp: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetGeoIp,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getGeoIp: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failureGetGeoIp,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getGeoIp: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.setAccountPopup,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          accountPopup: action.payload,
        };
      }
    )
    .addCase(
      IAccountActions.setOnboardingPopup,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          onboardingPopup: action.payload,
        };
      }
    )
    .addCase(
      IAccountActions.updateUser,
      (state, action: any): IAccountModel => {
        const { newValues } = action.payload;
        let user = state.user;

        if (defined(user)) {
          user = { ...user, ...newValues };
        }
        return { ...state, user };
      }
    )
    .addCase(
      IAccountActions.updateProviderConfig,
      (state, action: any): IAccountModel => {
        const { newValues } = action.payload;
        let providerConfig = state.providerConfig;

        if (defined(providerConfig)) {
          providerConfig = { ...providerConfig, ...newValues };
        }
        return { ...state, providerConfig };
      }
    )
    .addCase(
      IAccountActions.initGetSavedCreditCards,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getSavedCreditCards: {
            status: ERequestStatus.Fetching,
          },
          savedCreditCards: [],
        };
      }
    )
    .addCase(
      IAccountActions.successGetSavedCreditCards,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getSavedCreditCards: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          savedCreditCards: action.payload.data,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetSavedCreditCards,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getSavedCreditCards: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteSavedCreditCard,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteSavedCreditCard: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteSavedCreditCard,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteSavedCreditCard: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteSavedCreditCard,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteSavedCreditCard: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IHeaderModalActions.successPatchUpdateChatFavorite,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          user: {
            ...state.user!,
            has_favorites: true,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetSubscriptions,
      (state, action: any): IAccountModel => {
        // const { isInitialRequest } = action.payload.config;
        const nextState = {
          getSubscriptions: {
            status: ERequestStatus.Fetching,
          },
          // subscriptions: isInitialRequest ? null : state.subscriptions
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IAccountActions.successGetSubscriptions,
      (state, action: any): IAccountModel => {
        const config: IGetSubscriptionsConfig = action.payload.config;
        const res = action.payload.res;

        let subscriptions: ISubscriptionInfo[] = res.data;
        const getSubscriptionsNextUrl: string =
          res.data.length > 0 ? res.next : "";
        const subscriptionsTotal: number = res.total;

        if (!config.isInitialRequest) {
          subscriptions = [...(state.subscriptions || []), ...res.data];
        }

        return {
          ...state,
          nextSubscriptionsUrl: getSubscriptionsNextUrl,
          subscriptions,
          getSubscriptions: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
          subscriptionsTotal,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetSubscriptions,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getSubscriptions: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    // .addCase(IAccountActions.initDeleteSubscription, (state, action: any): IAccountModel => {
    //   return {
    //     ...state,
    //     deleteSubscription: {
    //       status: ERequestStatus.Fetching
    //     }
    //   };
    // })
    // .addCase(IAccountActions.successDeleteSubscription, (state, action: any): IAccountModel => {
    //   return {
    //     ...state,
    //     deleteSubscription: {
    //       responseData: action.payload.res,
    //       status: ERequestStatus.Fetched
    //     }
    //   };
    // })
    // .addCase(IAccountActions.failureDeleteSubscription, (state, action: any): IAccountModel => {
    //   return {
    //     ...state,
    //     deleteSubscription: {
    //       error: action.payload,
    //       status: ERequestStatus.Error
    //     }
    //   };
    // })
    .addCase(
      IAccountActions.initDeleteVideoGreeting,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteVideoGreeting: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteVideoGreeting,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteVideoGreeting: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteVideoGreeting,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteVideoGreeting: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.setManageSubscriptionsEditing,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          isManageSubscriptionsEditing: action.payload,
          // isManageSubscriptionsSelectAll: false, // reset
          manageSubscriptionsSelection: [], // reset
        };
      }
    )
    .addCase(
      IAccountActions.toggleManageSubscriptionsSubSelection,
      (state, action: any): IAccountModel => {
        const selectedSub: ISubscriptionInfo = action.payload;
        let manageSubscriptionsSelection: ISubscriptionInfo[];
        if (
          state.manageSubscriptionsSelection.findIndex(
            (sub) => sub.user._id === selectedSub.user._id
          ) !== -1
        ) {
          manageSubscriptionsSelection =
            state.manageSubscriptionsSelection.filter(
              (sub) => sub.user._id !== selectedSub.user._id
            );
        } else {
          manageSubscriptionsSelection = [
            ...state.manageSubscriptionsSelection,
            selectedSub,
          ];
        }
        return {
          ...state,
          manageSubscriptionsSelection,
        };
      }
    )
    .addCase(
      IAccountActions.toggleManageSubscriptionsAllSubsSelection,
      (state, action: any): IAccountModel => {
        const manageSubscriptionsSelection = (state.subscriptions || []).filter(
          (sub) => sub.status === ESubscriptionInfoStatus.ACTIVE
        );
        return {
          ...state,
          manageSubscriptionsSelection,
          // isManageSubscriptionsSelectAll: !state.isManageSubscriptionsSelectAll,
          // manageSubscriptionsSelection: []
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteSubscriptions,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteSubscriptions: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteSubscriptions,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteSubscriptions: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          isManageSubscriptionsEditing: false, // reset
          // isManageSubscriptionsSelectAll: false, // reset
          manageSubscriptionsSelection: [], // reset
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteSubscriptions,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteSubscriptions: {
            error: action.payload.error,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.resetDeleteSubscriptions,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteSubscriptions: requestInit(),
        };
      }
    )
    .addCase(
      IAccountActions.initPatchReenableSubscription,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchReenableSubscription: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPatchReenableSubscription,
      (state, action: any): IAccountModel => {
        const updatedSub: ISubscriptionInfo = action.payload.data[0];
        return {
          ...state,
          patchReenableSubscription: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          subscriptions: (state.subscriptions || []).map((sub) =>
            sub.user._id === updatedSub.user._id ? updatedSub : sub
          ),
        };
      }
    )
    .addCase(
      IAccountActions.failurePatchReenableSubscription,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchReenableSubscription: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.resetPatchReenableSubscription,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchReenableSubscription: requestInit(),
        };
      }
    )
    .addCase(
      IAccountActions.initPatchUserRandomAvatar,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getRandomAvatar: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPatchUserRandomAvatar,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getRandomAvatar: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          user: action.payload || state.user,
        };
      }
    )
    .addCase(
      IAccountActions.failurePatchUserRandomAvatar,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getRandomAvatar: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetAutoMessages,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getAutoMessages: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetAutoMessages,
      (state, action: any): IAccountModel => {
        const autoMessageCategories: IAutoMessageCategory[] =
          action.payload.data;

        // reporting of missing dimensions
        // (TODO: consider removing this later to improve performance)
        autoMessageCategories.forEach((category) => {
          category.messages.forEach((autoMessage) => {
            if (
              autoMessage.type === EAutoMessageType.Image ||
              autoMessage.type === EAutoMessageType.Video
            ) {
              if (!defined(autoMessage.media)) {
                sendWarningMessage(
                  `AutoMessage type \`${autoMessage.type}\` fetched but without media! \`${autoMessage._id}\``
                );
              } else if (
                !defined(autoMessage.media!.width) ||
                !defined(autoMessage.media!.height)
              ) {
                sendWarningMessage(
                  `AutoMessage fetched with media but without dimensions! \`${autoMessage._id}\``
                );
              }
            }
          });
        });

        return {
          ...state,
          autoMessageCategories,
          lastSavedAutoMessageCategories: autoMessageCategories, // replace with new
          getAutoMessages: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failureGetAutoMessages,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getAutoMessages: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initPostAutoMessage,
      (state, action: any): IAccountModel => {
        const { category, clientSideAutoMessage } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // add clientSideAutoMessage
              return {
                ...c,
                messages: [...c.messages, clientSideAutoMessage],
              };
            }
            return c;
          }),
          postAutoMessage: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.updatePostAutoMessageProgress,
      (state, action: any): IAccountModel => {
        const { category, progress, loaded, total, __clientSideSendId } =
          action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // update clientSideAutoMessage with new progress value
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m.__clientSideSendId === __clientSideSendId) {
                    return {
                      ...m,
                      media: {
                        ...m.media,
                        uploadProgress: progress,
                        loaded,
                        total,
                      },
                    };
                  }
                  return m;
                }),
              };
            }
            return c;
          }) as any,
        };
      }
    )
    .addCase(
      IAccountActions.successPostAutoMessage,
      (state, action: any): IAccountModel => {
        const { category, response, __clientSideSendId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // replace clientSideAutoMessage with API response
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m.__clientSideSendId === __clientSideSendId) {
                    return response.data;
                  }
                  return m;
                }),
              };
            }
            return c;
          }),
          postAutoMessage: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failurePostAutoMessage,
      (state, action: any): IAccountModel => {
        const { category, isAbort, errorResponse, __clientSideSendId } =
          action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              if (isAbort) {
                // remove clientSideAutoMessage
                return {
                  ...c,
                  messages: c.messages.filter(
                    (m) => m.__clientSideSendId !== __clientSideSendId
                  ),
                };
              } else {
                // update clientSideAutoMessage
                return {
                  ...c,
                  messages: c.messages.map((m) => {
                    if (m.__clientSideSendId === __clientSideSendId) {
                      return {
                        ...m,
                        __isUploadFailed: true,
                        __errorMessage:
                          (errorResponse && errorResponse.message) || "",
                      };
                    }
                    return m;
                  }),
                };
              }
            }
            return c;
          }),
          postAutoMessage: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteAutoMessage,
      (state, action: any): IAccountModel => {
        const { category, autoMessageId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // mark the message to be deleted as __isBeingDeleted
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m._id === autoMessageId) {
                    return {
                      ...m,
                      __isBeingDeleted: true,
                    };
                  }
                  return m;
                }),
              };
            }
            return c;
          }),
          deleteAutoMessage: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteAutoMessage,
      (state, action: any): IAccountModel => {
        const { category, autoMessageId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // remove deleted message
              return {
                ...c,
                messages: c.messages.filter((m) => m._id !== autoMessageId),
              };
            }
            return c;
          }),
          deleteAutoMessage: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteAutoMessage,
      (state, action: any): IAccountModel => {
        const { category, errorResponse, autoMessageId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m._id === autoMessageId) {
                    return {
                      ...m,
                      __isBeingDeleted: false,
                      __errorMessage:
                        (errorResponse && errorResponse.message) || "",
                    };
                  }
                  return m;
                }),
              };
            }
            return c;
          }),
          deleteAutoMessage: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initPutAutoMessages,
      (state, action: any): IAccountModel => {
        const { category } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // mark all messages in this category as __isBeingDeleted
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: true,
                })),
              };
            }
            return c;
          }),
          putAutoMessages: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPutAutoMessages,
      (state, action: any): IAccountModel => {
        const { category } = action.payload;
        const autoMessageCategories = state.autoMessageCategories.map((c) => {
          if (c.category === category) {
            // false the loading indicator for all messages in this category (__isBeingDeleted)
            return {
              ...c,
              messages: c.messages.map((m) => ({
                ...m,
                __isBeingDeleted: false,
              })),
            };
          }
          return c;
        });
        return {
          ...state,
          autoMessageCategories,
          lastSavedAutoMessageCategories: autoMessageCategories,
          putAutoMessages: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failurePutAutoMessages,
      (state, action: any): IAccountModel => {
        const { category, errorResponse } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: false,
                  __errorMessage:
                    (errorResponse && errorResponse.message) || "",
                })),
              };
            }
            return c;
          }),
          putAutoMessages: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteAutoMessages,
      (state, action: any): IAccountModel => {
        const { category } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // mark all messages in this category as __isBeingDeleted
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: true,
                })),
              };
            }
            return c;
          }),
          deleteAutoMessages: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteAutoMessages,
      (state, action: any): IAccountModel => {
        const { category } = action.payload;
        const autoMessageCategories = state.autoMessageCategories.map((c) => {
          if (c.category === category) {
            // empty this category
            return {
              ...c,
              excerpt: "",
              messages: [],
            };
          }
          return c;
        });
        return {
          ...state,
          autoMessageCategories,
          lastSavedAutoMessageCategories: autoMessageCategories,
          deleteAutoMessages: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteAutoMessages,
      (state, action: any): IAccountModel => {
        const { category, errorResponse } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: false,
                  __errorMessage:
                    (errorResponse && errorResponse.message) || "",
                })),
              };
            }
            return c;
          }),
          deleteAutoMessages: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.resetAutoMessages,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          autoMessageCategories: state.lastSavedAutoMessageCategories, // replace with old
        };
      }
    )
    .addCase(
      IAccountActions.initPostUserTag,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          postUserTag: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPostUserTag,
      (state, action: any): IAccountModel => {
        const { tag, response } = action.payload;
        return {
          ...state,
          user:
            state.user === null
              ? null
              : {
                  ...state.user,
                  tags: [...(state.user.tags || []), tag],
                },
          postUserTag: {
            responseData: response,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IAccountActions.failurePostUserTag,
      (state, action: any): IAccountModel => {
        const { errorResponse } = action.payload;
        return {
          ...state,
          postUserTag: {
            error: errorResponse,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetMyPurchases,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getMyPurchases: {
            status: ERequestStatus.Fetching,
          },
          myPurchases: [],
        };
      }
    )
    .addCase(
      IAccountActions.successGetMyPurchases,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getMyPurchases: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          myPurchases: action.payload.data,
          nextMyPurchasesUrl: action.payload.next,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetMyPurchases,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getMyPurchases: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetMyPurchasesNext,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getMyPurchasesNext: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetMyPurchasesNext,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getMyPurchasesNext: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          myPurchases: [...state.myPurchases, ...action.payload.data],
          nextMyPurchasesUrl: action.payload.next,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetMyPurchasesNext,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getMyPurchasesNext: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initPatchHideMyPurchase,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchHideMyPurchase: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPatchHideMyPurchase,
      (state, action: any): IAccountModel => {
        const { messageIds } = action.payload;
        return {
          ...state,
          patchHideMyPurchase: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          myPurchases: (state.myPurchases || []).filter(
            (purchase) => !messageIds.includes(purchase.message_id)
          ),
        };
      }
    )
    .addCase(
      IAccountActions.failurePatchHideMyPurchase,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchHideMyPurchase: {
            error: action.payload.errorResponse,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initPatchUnhideMyPurchase,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchUnhideMyPurchase: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successPatchUnhideMyPurchase,
      (state, action: any): IAccountModel => {
        const { messageIds } = action.payload;
        return {
          ...state,
          patchUnhideMyPurchase: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          myPurchases: (state.myPurchases || []).filter(
            (purchase) => !messageIds.includes(purchase.message_id)
          ),
        };
      }
    )
    .addCase(
      IAccountActions.failurePatchUnhideMyPurchase,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          patchUnhideMyPurchase: {
            error: action.payload.errorResponse,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.removeExpiredFromMyPurchases,
      (state, action: any): IAccountModel => {
        const myPurchases = state.myPurchases.filter(
          (purchase) => purchase.message_id !== action.payload.messageId
        );
        return {
          ...state,
          myPurchases,
        };
      }
    )
    .addCase(
      IAccountActions.initGetIdVerification,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getIdVerification: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetIdVerification,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getIdVerification: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          idVerification: action.payload,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetIdVerification,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getIdVerification: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetVaultMedia,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getVaultMedia: {
            status: ERequestStatus.Fetching,
          },
          vaultMedia: [],
        };
      }
    )
    .addCase(
      IAccountActions.successGetVaultMedia,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getVaultMedia: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          vaultMedia: action.payload.data || [],
          nextGetVaultMediaUrl: action.payload.next || "",
        };
      }
    )
    .addCase(
      IAccountActions.failureGetVaultMedia,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getVaultMedia: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initGetVaultMediaNext,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getVaultMediaNext: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetVaultMediaNext,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getVaultMediaNext: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          vaultMedia: [...state.vaultMedia, ...action.payload.data],
          nextGetVaultMediaUrl: action.payload.next,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetVaultMediaNext,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          getVaultMediaNext: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteVaultMedia,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteVaultMedia: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteVaultMedia,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteVaultMedia: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          vaultMedia: (state.vaultMedia || []).filter(
            (media) => !action.payload.mediaIds.includes(media._id)
          ),
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteVaultMedia,
      (state, action: any): IAccountModel => {
        return {
          ...state,
          deleteVaultMedia: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      ILandingActions.successDeleteImage,
      (state, action: any): IAccountModel => {
        const { public_id, media_id } = action.payload;

        if (state.user && state.user.featured) {
          const updatedFeatured = state.user.featured.filter((img) => {
            if (public_id && img.public_id === public_id) return false;
            if (media_id && img.media_id === media_id) return false;
            return true;
          });

          return {
            ...state,
            user: {
              ...state.user,
              featured: updatedFeatured,
            },
          };
        }

        return state;
      }
    );
});
